import { computed, customRef } from 'vue';
import { useComponentContext } from 'composables/useComponentContext';
import { useStateStore } from 'composables/useStateStore';
import type { MenuProps, MenuItemProps } from './menu.types';

const MENU_TYPE = 'Menu';
const MENU_ITEM_TYPE = 'MenuItem';

export const useMenu = () => {
  const context = useComponentContext<MenuItemProps | MenuProps<MenuItemProps>>();
  const stateStore = useStateStore<MenuItemProps | MenuProps<MenuItemProps>>();

  // Performance: Cache menuUid computation (only changes if component tree changes)
  const menuUid = computed(() => {
    if (context.typeName === MENU_TYPE) {
      return context.uid;
    }
    const parentMenu = stateStore.findParentOfType(context.uid as number, MENU_TYPE);
    return parentMenu?.uid;
  });

  // Find all child menu items for this menu (only MenuItem type, not other components)
  const items = computed(() =>
    menuUid.value ? stateStore.findChildrenByType(menuUid.value, MENU_ITEM_TYPE) : []
  );

  // Get menu props (useful for menu items to access parent menu configuration)
  const menuProps = computed(() => {
    if (!context.uid) return undefined;
    const menu = stateStore.findParentOfType(context.uid, MENU_TYPE);
    return menu?.props;
  });

  // Check if current item is a direct child of Menu (level 0)
  const isSubItem = computed(() => {
    if (!context.uid || context.typeName !== MENU_ITEM_TYPE) return false;
    const parent = stateStore.findParent(context.uid.toString());
    return parent?.type === MENU_TYPE;
  });

  // Performance: Cache level calculation with custom ref that only recomputes on state changes
  let cachedLevel: number | null = null;
  let lastStateVersion = 0;
  const currentLevel = customRef(track => {
    return {
      get() {
        track();

        // Use cached value if state hasn't changed (O(1) version check)
        const currentVersion = stateStore.getVersion();
        if (cachedLevel !== null && lastStateVersion === currentVersion) {
          return cachedLevel;
        }

        lastStateVersion = currentVersion;

        if (!context.uid || context.typeName !== MENU_ITEM_TYPE) {
          cachedLevel = 0;
          return 0;
        }

        let level = 0;
        let currentUid = context.uid.toString();
        let parent = stateStore.findParent(currentUid);

        while (parent) {
          if (parent.type === MENU_TYPE) break;
          if (parent.type === MENU_ITEM_TYPE) level++;
          currentUid = parent.uid?.toString() || '';
          parent = stateStore.findParent(currentUid);
        }

        cachedLevel = level;
        return level;
      },
      set() {
        // Read-only
      }
    };
  });

  // Helper to resolve itemUid - defaults to current context if it's a MenuItem
  function resolveItemUid(itemUid?: number): number {
    if (itemUid !== undefined) return itemUid;

    if (!context.uid) {
      throw new Error('No itemUid provided and no context.uid available.');
    }

    if (context.typeName === MENU_ITEM_TYPE) return context.uid;

    // Find nearest parent MenuItem
    const parentItem = stateStore.findParentOfType(context.uid, MENU_ITEM_TYPE);
    if (parentItem?.uid) return parentItem.uid;

    throw new Error('No itemUid provided and not called from or inside a MenuItem component.');
  }

  // Controller functions
  const open = (itemUid?: number) => {
    const uid = resolveItemUid(itemUid);
    stateStore.setPropValue(uid, 'open', true);
  };

  const close = (itemUid?: number) => {
    const uid = resolveItemUid(itemUid);
    stateStore.setPropValue(uid, 'open', false);
  };

  const toggle = (itemUid?: number) => {
    const uid = resolveItemUid(itemUid);
    const item = stateStore.getByUid(uid);
    if (item) {
      stateStore.setPropValue(uid, 'open', !item.props.open);
    }
  };

  const isOpen = computed(() => {
    if (context.typeName !== MENU_ITEM_TYPE || !context.uid) return false;
    const item = stateStore.getByUid(context.uid);
    return item ? !!item.props.open : false;
  });

  // Performance: Cache hasChildren with custom ref
  let cachedHasChildren: boolean | null = null;
  let lastChildrenCheck = 0;
  const hasChildren = customRef(track => {
    return {
      get() {
        track();

        if (context.typeName !== MENU_ITEM_TYPE || !context.uid) return false;

        // Use cached value if state hasn't changed (O(1) version check)
        const currentVersion = stateStore.getVersion();
        if (cachedHasChildren !== null && lastChildrenCheck === currentVersion) {
          return cachedHasChildren;
        }

        lastChildrenCheck = currentVersion;

        // Only look for MenuItem children, not other component types like Icon
        const registeredChildren = stateStore.findChildrenByType(context.uid, MENU_ITEM_TYPE);
        cachedHasChildren = registeredChildren && registeredChildren.length > 0;
        return cachedHasChildren;
      },
      set() {
        // Read-only
      }
    };
  });

  // Check if current item is a direct child (level 1) of a parent MenuItem
  const isChild = computed(() => currentLevel.value === 1);

  // Check if current item is expandable (only level 0 items with children can expand)
  const isExpandable = computed(() => {
    return hasChildren.value && currentLevel.value === 0;
  });

  return {
    // Properties for MenuItem context
    menuProps,
    isSubItem,
    currentLevel,
    isChild,
    isOpen,
    hasChildren,
    isExpandable,

    // Properties for Menu context
    items,
    menuUid,

    // Controller functions
    open,
    close,
    toggle
  };
};
